/**
 * Thermal receipt printer — 80mm roll format.
 * Opens a new window, writes the receipt HTML, triggers window.print().
 */

export function printThermalReceipt(sale, tenant, formatMoney) {
  const li = sale.line_items || [];
  const taxMode = sale.tax_mode_snapshot || (sale.vat ? 'VAT' : 'IN');

  // Tax totals
  let totalCgst = 0, totalSgst = 0, totalIgst = 0, totalVat = 0;
  let hasSnapshots = false;
  for (const l of li) {
    const tb = l.tax_breakdown_snapshot;
    if (tb) { hasSnapshots = true; totalCgst += tb.cgst || 0; totalSgst += tb.sgst || 0; totalIgst += tb.igst || 0; totalVat += tb.vat || 0; }
  }
  if (!hasSnapshots) { totalCgst = sale.cgst || 0; totalSgst = sale.sgst || 0; totalIgst = sale.igst || 0; totalVat = sale.vat || 0; }

  const fmt = (v) => {
    try { return formatMoney ? formatMoney(v) : `₹${Number(v || 0).toFixed(2)}`; }
    catch { return String(v || 0); }
  };

  const truncate = (s, max = 26) => s && s.length > max ? s.slice(0, max - 1) + '…' : (s || '');
  const dash = '<tr><td colspan="3" style="border-top:1px dashed #888;padding:3px 0"></td></tr>';

  const dateStr = sale.sale_date
    ? new Date(sale.sale_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  const timeStr = sale.created_at
    ? new Date(sale.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
    : '';

  // Item rows
  const itemRows = li.map(l => {
    const name = truncate(l.product_name || l.name || 'Item');
    const qty = l.qty || 0;
    const price = l.unit_price || 0;
    const lineTotal = l.line_total || qty * price;
    return `
      <tr><td colspan="3" style="font-size:11px;padding:1px 0">${name}</td></tr>
      <tr>
        <td style="font-size:10px;color:#555;padding:0 0 2px 4px">${qty} × ${fmt(price)}</td>
        <td></td>
        <td style="text-align:right;font-size:11px;font-weight:600">${fmt(lineTotal)}</td>
      </tr>`;
  }).join('');

  // Tax rows
  let taxRows = '';
  if (taxMode === 'IN') {
    if (totalCgst > 0 || totalSgst > 0) {
      taxRows += `<tr><td colspan="2" style="font-size:10px">CGST</td><td style="text-align:right;font-size:10px">${fmt(totalCgst)}</td></tr>`;
      taxRows += `<tr><td colspan="2" style="font-size:10px">SGST</td><td style="text-align:right;font-size:10px">${fmt(totalSgst)}</td></tr>`;
    }
    if (totalIgst > 0) {
      taxRows += `<tr><td colspan="2" style="font-size:10px">IGST</td><td style="text-align:right;font-size:10px">${fmt(totalIgst)}</td></tr>`;
    }
  } else {
    if (totalVat > 0) {
      taxRows += `<tr><td colspan="2" style="font-size:10px">${sale.tax_label_snapshot || 'VAT'}</td><td style="text-align:right;font-size:10px">${fmt(totalVat)}</td></tr>`;
    }
  }

  const storeName = tenant?.name || 'Store';
  const storeAddress = tenant?.address || '';
  const gstin = tenant?.gstin || tenant?.trn || '';
  const paymentStatus = (sale.payment_status || 'unpaid').toUpperCase();
  const modeMap = { cash: 'Cash', card: 'Card', upi: 'UPI', wallet: 'Wallet', other: 'Other' };
  const modeLabel = sale.payment_mode ? (modeMap[sale.payment_mode] || sale.payment_mode) : '—';
  const paymentLine = `${modeLabel} · ${paymentStatus}`;

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Receipt ${sale.invoice_number || ''}</title>
<style>
  @page { size: 80mm auto; margin: 2mm; }
  * { margin:0; padding:0; box-sizing:border-box; }
  body { width:80mm; font-family:'Courier New',Courier,monospace; font-size:11px; color:#111; padding:2mm; }
  table { width:100%; border-collapse:collapse; }
  .center { text-align:center; }
  .bold { font-weight:700; }
  .big { font-size:14px; }
  .small { font-size:9px; color:#666; }
  .muted { color:#888; }
  .right { text-align:right; }
</style></head><body>
<table>
  <tr><td class="center bold big" style="padding:2px 0">${storeName}</td></tr>
  ${storeAddress ? `<tr><td class="center small" style="padding:0 8px;word-wrap:break-word;max-width:280px">${truncate(storeAddress, 60)}</td></tr>` : ''}
  ${gstin ? `<tr><td class="center small" style="padding:1px 0">GSTIN: ${gstin}</td></tr>` : ''}
  ${dash}
  <tr>
    <td style="font-size:10px">${sale.invoice_number || 'Receipt'}</td>
    <td></td>
    <td class="right" style="font-size:10px">${dateStr} ${timeStr}</td>
  </tr>
  ${sale.customer_name ? `<tr><td colspan="3" style="font-size:10px">Customer: ${truncate(sale.customer_name, 30)}</td></tr>` : ''}
  ${dash}
  ${itemRows}
  ${dash}
  <tr><td colspan="2" style="font-size:10px">Subtotal</td><td class="right" style="font-size:10px">${fmt(sale.subtotal)}</td></tr>
  ${taxRows}
  <tr><td colspan="2" class="bold" style="font-size:12px;padding-top:3px">TOTAL</td><td class="right bold" style="font-size:12px;padding-top:3px">${fmt(sale.total)}</td></tr>
  ${dash}
  <tr><td colspan="3" style="font-size:10px;padding:2px 0">Payment: ${paymentLine}${sale.amount_paid > 0 && sale.amount_paid < sale.total ? ` (Paid: ${fmt(sale.amount_paid)})` : ''}</td></tr>
  ${dash}
  <tr><td class="center" style="padding:4px 0;font-size:10px">Thank you for your purchase!</td></tr>
  <tr><td class="center small muted" style="padding:1px 0">Powered by AI-BM</td></tr>
</table>
<script>
  window.onload = function() {
    setTimeout(function() { window.print(); }, 80);
  };
  window.onafterprint = function() { window.close(); };
</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=320,height=600');
  if (w) { w.document.write(html); w.document.close(); }
}
